/**
 * ImageWrapperManager
 *
 * Watches Summernote editor DOM changes and keeps `.img-wrap` wrappers in sync
 * with image (`<img>`) state.
 *
 * Responsibilities:
 * - Removes empty `.img-wrap` when an image is deleted
 * - Syncs image classes and layout-related styles (width, float) to the wrapper
 * - Triggers input event on the editable element when changes occur
 *
 * Designed for Vue 2 + Summernote environments
 */
export const ImageWrapperManager = (context) => {
    const $editor = $('.note-editable');
    if (!$editor.length) return;

    /**
     * Triggers input event on the editor
     */
    const triggerInputEvent = () => {
        if (context && context.layoutInfo && context.layoutInfo.editable) {
            $(context.layoutInfo.editable).trigger('input');
        }
    };

    /**
     * Extracts a specific CSS property from inline style string
     */
    const extractStyle = (style, property) => {
        const match = style.match(new RegExp(`${property}:\\s*([^;]+)`));
        return match ? match[1] : null;
    };

    /**
     * Synchronizes wrapper classes and styles with the image
     */
    const syncWrapperWithImage = ($img) => {
        const $wrapper = $img.closest('.img-wrap');
        if (!$wrapper.length) return;

        // Sync classes (ignore Summernote selection class)
        const imageClasses = ($img.attr('class') || '')
            .replace('note-selected', '')
            .trim();

        $wrapper.attr('class', imageClasses ? `img-wrap ${imageClasses}` : 'img-wrap');

        // Sync width and float styles
        const imgStyle = $img.attr('style') || '';
        const width = extractStyle(imgStyle, 'width');
        const float = extractStyle(imgStyle, 'float');

        const wrapperStyle = [
            width && `width:${width}`,
            float && `float:${float}`
        ].filter(Boolean).join(';');

        if (wrapperStyle) {
            $wrapper.attr('style', wrapperStyle);
        } else {
            $wrapper.removeAttr('style');
        }
    };

    /**
     * Removes wrapper if it no longer contains an image
     */
    const cleanupEmptyWrapper = (mutation) => {
        mutation.removedNodes.forEach((node) => {
            if (node.nodeType !== 1 || node.tagName !== 'IMG') return;

            const $wrapper = $(mutation.target).closest('.img-wrap');

            if ($wrapper.length && !$wrapper.find('img').length) {
                $wrapper.remove();
                triggerInputEvent();
            }
        });
    };

    /**
     * MutationObserver handler
     */
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            // Image removal handling
            if (mutation.type === 'childList' && mutation.removedNodes.length) {
                cleanupEmptyWrapper(mutation);
            }

            // Image attribute changes (class / style)
            if (
                mutation.type === 'attributes' &&
                mutation.target.tagName === 'IMG'
            ) {
                syncWrapperWithImage($(mutation.target));
                triggerInputEvent();
            }

            // Handle image insertion (new images wrapped)
            if (mutation.type === 'childList' && mutation.addedNodes.length) {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1 && node.tagName === 'IMG') {
                        triggerInputEvent();
                    }
                });
            }
        });
    });

    // Start observing editor changes
    observer.observe($editor[0], {
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
        subtree: true
    });
};